import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  QA_NAME_SHOW_LESS_BUTTON,
  QA_NAME_SHOW_MORE_BUTTON,
} from '../../../../../constants/qa'
import { useReadMore } from '../../../../../hooks'
import { getQaSelector } from '../../../../../utils/common'
import Button, { BUTTON_HEIGHT_24, BUTTON_HEIGHT_32 } from '../../../../Button'
import Text from '../../../../Text'
import { TextareaElement } from '../../../../Textarea'
import { Widget } from '../../../../Widget'
import { useApplicantProfileContext } from '../../RentalPortalApplicantProfile.hooks'

import {
  APPLICANT_PROFILE_NOTE_CLAMP_LINES,
  QA_NAME_APPLICANT_NOTES_BLOCK,
  QA_NAME_APPLICANT_PROFILE_NOTE,
  QA_NAME_APPLICANT_PROFILE_NOTE_CELL,
  QA_NAME_APPLICANT_PROFILE_NOTE_EDIT,
  QA_NAME_APPLICANT_PROFILE_NOTE_INPUT,
} from './ApplicantNotesBlock.constants'
import { getCanEditApplicantNote } from './ApplicantNotesBlock.utils'

import * as styles from './ApplicantNotesBlock.module.css'

import EditIcon from '../../../../../icons/edit.svg'
import PlusIcon from '../../../../../icons/plus.svg'

function ApplicantNotesBlock() {
  const { t } = useTranslation()

  const { applicantData, isInitiated, isPreview, noteEdit } =
    useApplicantProfileContext()

  const {
    editContainerRef,
    editValue,
    handleChangeEditValue,
    handleSaveEditValue,
    handleStartEditing,
    isEditing,
    isSaving,
  } = noteEdit

  const { handleClickReadMore, isReadMore, showReadMore, textRef } =
    useReadMore({
      maxLines: APPLICANT_PROFILE_NOTE_CLAMP_LINES,
      textData: applicantData?.content,
    })

  const canEdit = getCanEditApplicantNote({ applicantData, isPreview })

  return (
    <Widget
      headerProps={{ contentProps: { actionsProps: { EditButton: null } } }}
      isSkeleton={!isInitiated || !applicantData}
      qaName={QA_NAME_APPLICANT_NOTES_BLOCK}
      title={t('RentalPortal.ApplicantProfileNoteLabelKey')}
    >
      <div className={styles.ApplicantNotesBlock}>
        {isEditing ? (
          <div className={styles.editWrapper} ref={editContainerRef}>
            <TextareaElement
              autoResizeHeight
              focused
              disabled={isSaving}
              placeholder={t('RentalPortal.ApplicantProfileNotePlaceholderKey')}
              qaName={QA_NAME_APPLICANT_PROFILE_NOTE_INPUT}
              value={editValue}
              onBlur={handleSaveEditValue}
              onChange={handleChangeEditValue}
            />
          </div>
        ) : (
          <>
            <div
              {...(canEdit
                ? {
                    ...getQaSelector({
                      value: QA_NAME_APPLICANT_PROFILE_NOTE_CELL,
                    }),
                    onClick: handleStartEditing,
                  }
                : null)}
              className={classNames(styles.noteRow, {
                [styles.editableNoteRow]: canEdit,
              })}
            >
              {canEdit ? (
                <Button.Outlined
                  isGrey
                  beforeIconProps={{
                    Icon: applicantData?.content ? EditIcon : PlusIcon,
                  }}
                  className={classNames(styles.editButton, {
                    [styles.persistentEditButton]: !applicantData?.content,
                  })}
                  height={BUTTON_HEIGHT_32}
                  qaName={QA_NAME_APPLICANT_PROFILE_NOTE_EDIT}
                  tooltip={t(
                    applicantData?.content
                      ? 'Profile.EditInfoItemBtnTooltipKey'
                      : 'Profile.AddInfoItemBtnTooltipKey'
                  )}
                  onClick={handleStartEditing}
                />
              ) : null}
              {applicantData?.content ? (
                <Text
                  className={styles.note}
                  qaName={QA_NAME_APPLICANT_PROFILE_NOTE}
                  ref={textRef}
                >
                  {applicantData.content}
                </Text>
              ) : (
                <Text
                  disabled
                  isSecondary
                  qaName={QA_NAME_APPLICANT_PROFILE_NOTE}
                >
                  {t('RentalPortal.ApplicantProfileNoteEmptyKey')}
                </Text>
              )}
            </div>
            {showReadMore ? (
              <Button.Contained
                isGrey
                height={BUTTON_HEIGHT_24}
                qaName={
                  isReadMore
                    ? QA_NAME_SHOW_LESS_BUTTON
                    : QA_NAME_SHOW_MORE_BUTTON
                }
                text={t(
                  isReadMore
                    ? 'Notification.CommonReadLessBtnKey'
                    : 'Notification.CommonReadMoreBtnKey'
                )}
                onClick={handleClickReadMore}
              />
            ) : null}
          </>
        )}
      </div>
    </Widget>
  )
}

export default ApplicantNotesBlock
